import React, { useMemo, useState } from 'react'
import { CheckCircle2, MessageSquarePlus } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { Theme } from '@/types/theme'
import { GoToHubSpotButton } from '@/components/ui/GoToHubSpotButton'
import type { DashboardSnapshot, TodayItem, TodayItemKind, TodayPanel as TodayPanelType, UnavailablePanel } from '../types'
import { useStartZephChat, type ZephChatSeed } from '../hooks/useStartZephChat'
import { formatAmount, todayChatSeed, todayItemHubSpotUrl } from '../lib/todaySeeds'
import EmptyWorkspaceState from './EmptyWorkspaceState'

interface TodayPanelProps {
  panel: TodayPanelType | UnavailablePanel
  hubspot: DashboardSnapshot['hubspot']
  // Approving here resumes the same conversation the chat would. Absent while the
  // approvals index isn't wired — the rows simply won't appear.
  onResolveApproval: (conversationId: string, action: 'approved' | 'rejected') => Promise<{ ok: boolean }>
}

type Filter = 'all' | TodayItemKind

// Chip order follows the ranked list itself, so scanning the filters and scanning
// the rows tell the same story.
const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'approval', label: 'Approvals' },
  { key: 'task', label: 'Tasks' },
  { key: 'lead', label: 'Leads' },
  { key: 'ticket', label: 'Tickets' },
  { key: 'deal', label: 'Deals' },
]

// Module-level so the "nothing to show" case is referentially stable across renders.
const EMPTY_ITEMS: TodayItem[] = []

const KIND_LABEL: Record<TodayItemKind, string> = {
  approval: 'Approve',
  task: 'Task',
  ticket: 'Ticket',
  lead: 'Lead',
  deal: 'Deal',
}

// Every colour is a theme token — the badge palette reuses the same semantic
// backgrounds the rest of the app already uses for these meanings.
function kindColors(kind: TodayItemKind, t: Theme): { backgroundColor: string; color: string } {
  switch (kind) {
    case 'approval':
      return { backgroundColor: t.purpleBackground, color: t.purpleText }
    case 'task':
      return { backgroundColor: t.infoBackground, color: t.infoText }
    case 'ticket':
      return { backgroundColor: t.warningBackground, color: t.warningText }
    case 'lead':
      return { backgroundColor: t.successBackground, color: t.successText }
    case 'deal':
      return { backgroundColor: t.sectionBackground, color: t.secondrytext }
  }
}

// A deal row's headline number only exists once HubSpot gave us an amount.
function dealAmountSuffix(item: TodayItem): string {
  if (item.kind !== 'deal') return ''
  const formatted = formatAmount(item.detail.amount, item.detail.currencyCode)
  return formatted ? ` · ${formatted}` : ''
}

const Row: React.FC<{
  item: TodayItem
  hubspot: DashboardSnapshot['hubspot']
  onStartChat: (seed: ZephChatSeed) => void
  onResolveApproval: TodayPanelProps['onResolveApproval']
}> = ({ item, hubspot, onStartChat, onResolveApproval }) => {
  const { currentTheme: t } = useTheme()
  const badge = kindColors(item.kind, t)
  const hubspotUrl = todayItemHubSpotUrl(item, hubspot)
  const [deciding, setDeciding] = useState(false)

  const decide = async (action: 'approved' | 'rejected') => {
    if (item.kind !== 'approval') return
    setDeciding(true)
    try {
      await onResolveApproval(item.detail.conversationId, action)
    } finally {
      setDeciding(false)
    }
  }

  // BUG-009: the row already hover-highlighted like it was clickable, but nothing
  // outside the small buttons actually was. Wires the row itself to the same
  // "open in HubSpot" action the button already offers — an approval row has no
  // HubSpot record of its own, so it keeps its plain (non-interactive) cursor.
  const openRecord = () => {
    if (hubspotUrl) window.open(hubspotUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 border-t transition-colors duration-150"
      style={{ borderColor: t.border, cursor: hubspotUrl ? 'pointer' : undefined }}
      role={hubspotUrl ? 'button' : undefined}
      tabIndex={hubspotUrl ? 0 : undefined}
      onClick={hubspotUrl ? openRecord : undefined}
      onKeyDown={
        hubspotUrl
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openRecord()
              }
            }
          : undefined
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = t.hoverBackground
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      <span
        className="shrink-0 mt-0.5 rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider"
        style={badge}
      >
        {KIND_LABEL[item.kind]}
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-medium truncate" style={{ color: t.text }} title={item.title}>
          {item.title}
          {dealAmountSuffix(item)}
        </p>
        <p className="text-[11.5px] truncate" style={{ color: t.secondrytext }} title={item.subtitle}>
          {item.emphasis && (
            <>
              <span className="font-bold" style={{ color: t.warningText }}>
                {item.emphasis}
              </span>
              {item.subtitle ? ' · ' : ''}
            </>
          )}
          {item.subtitle}
        </p>
      </div>

      <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        {item.kind === 'approval' && (
          <>
            <button
              type="button"
              onClick={() => void decide('approved')}
              disabled={deciding}
              className="rounded-md px-2 py-1 text-[11px] font-semibold transition-opacity hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: t.accent, color: t.buttonText }}
            >
              {deciding ? '…' : 'Approve'}
            </button>
            <button
              type="button"
              onClick={() => void decide('rejected')}
              disabled={deciding}
              className="rounded-md border px-2 py-1 text-[11px] font-medium transition-opacity hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: t.border, color: t.secondrytext, backgroundColor: t.secondary }}
            >
              Reject
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => onStartChat(todayChatSeed(item, hubspot))}
          title="Chat with Zeph about this"
          aria-label={`Chat with Zeph about "${item.title}"`}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition-opacity hover:opacity-80"
          style={{ color: t.accent, backgroundColor: `${t.accent}14` }}
        >
          <MessageSquarePlus size={12} />
          <span className="hidden sm:inline">Chat</span>
        </button>
        {item.kind !== 'approval' && (
          <GoToHubSpotButton href={hubspotUrl} label="HubSpot" className="!px-2 !py-1" />
        )}
      </div>
    </div>
  )
}

const TodayPanel: React.FC<TodayPanelProps> = ({ panel, hubspot, onResolveApproval }) => {
  const { currentTheme: t } = useTheme()
  const startZephChat = useStartZephChat()
  const [filter, setFilter] = useState<Filter>('all')

  const items = panel.status === 'ready' ? panel.items : EMPTY_ITEMS
  const counts = panel.status === 'ready' ? panel.counts : null

  // Keyed off `panel` rather than the derived `items`, so the memo is not
  // invalidated by a fresh empty array on every unavailable render.
  const visible = useMemo(() => {
    const all = panel.status === 'ready' ? panel.items : EMPTY_ITEMS
    return filter === 'all' ? all : all.filter((i) => i.kind === filter)
  }, [panel, filter])

  // A chip for a kind with nothing in it would be dead weight; "All" always shows.
  const chips = FILTERS.filter((f) => f.key === 'all' || (counts?.[f.key] ?? 0) > 0)

  return (
    <section
      className="rounded-2xl border overflow-hidden flex flex-col min-w-0"
      style={{ backgroundColor: t.secondary, borderColor: t.border, boxShadow: t.cardShadow }}
      aria-label="Today"
    >
      <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2.5">
        <h2 className="text-[13px] font-semibold" style={{ color: t.text }}>
          Today
        </h2>
        <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: t.mutedText }}>
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      {panel.status === 'ready' && items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-3">
          {chips.map((f) => {
            const active = filter === f.key
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f.key)}
                className="rounded-full border px-2.5 py-[3px] text-[11px] font-semibold transition-colors duration-150"
                style={{
                  backgroundColor: active ? t.accent : t.sectionBackground,
                  borderColor: active ? t.accent : t.border,
                  color: active ? t.buttonText : t.secondrytext,
                }}
              >
                {f.label}
                {f.key !== 'all' && counts ? ` ${counts[f.key]}` : ''}
              </button>
            )
          })}
        </div>
      )}

      {panel.status === 'unavailable' ? (
        <div className="px-4 pb-4">
          <EmptyWorkspaceState reason={panel.reason} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-10 px-4 text-center">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl"
            style={{ backgroundColor: t.successBackground }}
          >
            <CheckCircle2 size={20} style={{ color: t.successText }} />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: t.text }}>
              Nothing needs you right now
            </p>
            <p className="text-xs mt-1" style={{ color: t.mutedText }}>
              No approvals, overdue work or stale deals. I&apos;ll speak up when that changes.
            </p>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <p className="px-4 py-8 text-center text-xs" style={{ color: t.mutedText }}>
          Nothing in this filter.
        </p>
      ) : (
        <div className="max-h-[32rem] overflow-y-auto custom-scrollbar">
          {visible.map((item) => (
            <Row
              key={item.id}
              item={item}
              hubspot={hubspot}
              onStartChat={startZephChat}
              onResolveApproval={onResolveApproval}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default React.memo(TodayPanel)
